// Use Vite to bundle and serve the pdf.js worker locally (no CDN)
// This resolves to a URL emitted by the bundler
// eslint-disable-next-line import/no-unresolved
import workerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?worker&url'
import { useEffect, useRef } from 'react'

// Configure standard fonts path at runtime after dynamic import

interface PdfViewerProps {
  pdfUrl: string
  scale?: number
  onPagesConverted?: (pngDataUrls: string[]) => void
  onError?: (error: Error) => void
  onProgress?: (current: number, total: number) => void
}

// Standalone conversion function
export async function convertPdfToPngsConverter(
  pdfUrl: string,
  options?: {
    scale?: number
    onProgress?: (current: number, total: number) => void
  }
): Promise<string[]> {
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')

  if (!context) {
    throw new Error('Could not get canvas context')
  }

  try {
    // Dynamically import pdfjs to avoid loading browser-only APIs during test environments
    const pdfjsLib = await import('pdfjs-dist')

    // Use bundled worker asset (no CDN)
    const STANDARD_FONTS_PATH = '/pdfjs/standard_fonts/'
    const CMAP_URL = '/pdfjs/cmaps/'

    pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc

    // Load the PDF with proper error handling
    const loadingTask = pdfjsLib.getDocument({
      url: pdfUrl,
      standardFontDataUrl: STANDARD_FONTS_PATH,
      cMapUrl: CMAP_URL,
      cMapPacked: true,
      fontExtraProperties: true,
    })

    const pdf = await loadingTask.promise
    const totalPages = pdf.numPages
    const pngDataUrls: string[] = []

    for (let pageNumber = 1; pageNumber <= totalPages; pageNumber++) {
      try {
        const page = await pdf.getPage(pageNumber)
        const viewport = page.getViewport({ scale: options?.scale || 2 })

        canvas.width = viewport.width
        canvas.height = viewport.height

        await page.render({
          canvasContext: context,
          canvas: canvas,
          viewport,
        }).promise

        const pngDataUrl = canvas.toDataURL('image/png')
        pngDataUrls.push(pngDataUrl)

        options?.onProgress?.(pageNumber, totalPages)
      } catch (error) {
        console.error(`Error converting page ${pageNumber}:`, error)
        throw error
      }
    }

    return pngDataUrls
  } catch (error) {
    console.error('Error converting PDF to PNGs:', error)
    throw error
  } finally {
    // Clean up
    canvas.width = 0
    canvas.height = 0
  }
}

export function PdfToPngConverter({
  pdfUrl,
  scale = 2,
  onPagesConverted,
  onError,
  onProgress,
}: PdfViewerProps): React.JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    const convertPdf = async () => {
      try {
        const pngDataUrls = await convertPdfToPngsConverter(pdfUrl, {
          scale,
          onProgress,
        })
        onPagesConverted?.(pngDataUrls)
      } catch (error) {
        console.error('Error in PdfViewer:', error)
        onError?.(error as Error)
      }
    }

    convertPdf()
  }, [pdfUrl, scale, onPagesConverted, onError, onProgress])

  // Return an empty canvas - actual conversion happens in useEffect
  return <canvas ref={canvasRef} style={{ display: 'none' }} />
}
